<template>
    <div class="col-md-12">
        <div class="card">
            <div class="card-header pb-0 d-flex justify-content-between align-items-center no-print">
                <div>
                    <h4>Estado de Cuenta</h4>
                </div>
                <div class="btn-group" role="group">
                    <button type="button" class="btn btn-sm btn-primary" :disabled="loading || !movimientos.length" @click="downloadPDF">
                        <vue-feather type="download" size="14"></vue-feather> Descargar
                    </button>
                    <button type="button" class="btn btn-sm btn-secondary" :disabled="loading || !movimientos.length" @click="print">
                        <vue-feather type="printer" size="14"></vue-feather> Imprimir
                    </button>
                </div>
            </div>

            <div class="card-body">
                <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status"></div>
                    <p class="mt-2 text-muted">Cargando estado de cuenta...</p>
                </div>
                <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

                <div v-else-if="movimientos.length" ref="pdfContent">

                    <!-- ── TARJETA 1: Encabezado y resumen ───────────────── -->
                    <div class="card shadow-sm mb-4">
                        <div class="card-body">
                            <div class="row mb-3 align-items-center">
                                <div class="col-md-6">
                                    <img :src="require('@/assets/images/logo/logo-login.png')" alt="SIMM" style="height: 52px;">
                                </div>
                                <div class="col-md-6 text-end">
                                    <h5 class="mb-0 fw-bold">Estado de Cuenta</h5>
                                    <p class="text-muted small">Póliza: {{ $route.params.id }}</p>
                                    <small class="text-muted">Fecha de reporte: {{ hoy }}</small>
                                </div>
                            </div>

                            <table class="table table-sm table-bordered mb-3" style="width: auto; min-width: 380px;">
                                <tbody>
                                    <tr>
                                        <td class="fw-bold bg-light" style="width: 180px;">Periodo vigencia:</td>
                                        <td>{{ formatDate(fechaInicio) }} – {{ formatDate(fechaFin) }}</td>
                                    </tr>
                                    <tr>
                                        <td class="fw-bold bg-light">Total movimientos:</td>
                                        <td>{{ movimientos.length }}</td>
                                    </tr>
                                </tbody>
                            </table>

                            <div class="table-responsive">
                                <table class="table table-bordered table-sm mb-0">
                                    <thead class="table-secondary">
                                        <tr>
                                            <th>Total cargos</th>
                                            <th class="text-end">Total abonos</th>
                                            <th class="text-end">Saldo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td class="text-end">{{ formatCurrency(totalCargos) }}</td>
                                            <td class="text-end">{{ formatCurrency(totalPagos) }}</td>
                                            <td class="text-end fw-bold" :class="saldo > 0 ? 'text-danger' : 'text-success'">
                                                {{ formatCurrency(saldo) }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <!-- ── TARJETA 2: Movimientos ─────────────────────────── -->
                    <div class="card shadow-sm">
                        <div class="card-header">
                            <h6 class="mb-0 fw-bold">Movimientos por periodo</h6>
                        </div>
                        <div class="card-body p-0">
                            <div class="table-responsive">
                                <table class="table table-bordered table-sm mb-0">
                                    <thead class="table-secondary">
                                        <tr>
                                            <th style="min-width: 220px;">Concepto</th>
                                            <th class="text-end" style="min-width: 130px;">Cargos</th>
                                            <th class="text-end" style="min-width: 130px;">Abonos</th>
                                            <th class="text-center" style="min-width: 110px;">Fecha</th>
                                            <th style="min-width: 160px;">Observaciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="mov in movimientos" :key="mov.id">
                                            <td>{{ mov.concepto }}</td>
                                            <td class="text-end">{{ mov.debe ? formatCurrency(mov.monto) : '$ -' }}</td>
                                            <td class="text-end">{{ !mov.debe ? formatCurrency(mov.monto) : '$ -' }}</td>
                                            <td class="text-center">{{ formatDateShort(mov.fechaAplicacion) }}</td>
                                            <td>{{ mov.observaciones || '' }}</td>
                                        </tr>
                                        <tr class="table-secondary fw-bold">
                                            <td>Totales</td>
                                            <td class="text-end">{{ formatCurrency(totalCargos) }}</td>
                                            <td class="text-end">{{ formatCurrency(totalPagos) }}</td>
                                            <td></td>
                                            <td></td>
                                        </tr>
                                        <tr class="table-light fw-bold">
                                            <td>Saldo</td>
                                            <td colspan="2" class="text-end" :class="saldo > 0 ? 'text-danger' : 'text-success'">
                                                {{ formatCurrency(saldo) }}
                                            </td>
                                            <td></td>
                                            <td></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div class="print-footer mt-5 pt-3 border-top text-center text-muted">
                        <small>Documento generado el {{ hoy }} · SIMM v.2.0</small>
                    </div>
                </div>

                <div v-else class="text-center py-4 text-muted">
                    No se encontraron movimientos para esta póliza.
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/axios';
import { fetchAllItems } from '@/utils/listResponse';
import html2pdf from 'html2pdf.js';

function getField(item, key) {
    const found = Object.keys(item).find(k => k.toLowerCase() === key.toLowerCase());
    return found ? item[found] : undefined;
}

// TODO: confirmar con backend el catálogo real de estados de parcialidad.
const ESTADOS_PARCIALIDAD = {
    0: 'Pendiente',
    1: 'Pagada',
    2: 'Vencida',
    3: 'Cancelada',
};

function estadoParcialidadLabel(value) {
    const key = Number(value);
    return Object.prototype.hasOwnProperty.call(ESTADOS_PARCIALIDAD, key) ? ESTADOS_PARCIALIDAD[key] : '';
}

export default {
    name: 'EstadoCuenta',
    data() {
        return {
            movimientos: [],
            loading: false,
            error: null,
        };
    },
    computed: {
        hoy() {
            return new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
        },
        totalCargos() {
            return this.movimientos
                .filter(m => m.debe)
                .reduce((sum, m) => sum + (m.monto ?? 0), 0);
        },
        totalPagos() {
            return this.movimientos
                .filter(m => !m.debe)
                .reduce((sum, m) => sum + (m.monto ?? 0), 0);
        },
        saldo() {
            return this.totalCargos - this.totalPagos;
        },
        fechaInicio() {
            if (!this.movimientos.length) return null;
            return this.movimientos.reduce((min, m) =>
                m.fechaAplicacion < min ? m.fechaAplicacion : min,
                this.movimientos[0].fechaAplicacion
            );
        },
        fechaFin() {
            if (!this.movimientos.length) return null;
            return this.movimientos.reduce((max, m) =>
                m.fechaAplicacion > max ? m.fechaAplicacion : max,
                this.movimientos[0].fechaAplicacion
            );
        },
    },
    mounted() {
        this.fetchEstadoCuenta();
    },
    methods: {
        async fetchEstadoCuenta() {
            try {
                this.loading = true;
                this.error = null;

                const polizaRes = await api.get(`wizard/poliza/${this.$route.params.id}`);
                const polizaVersionId = polizaRes.data?.polizaVersionId;
                if (!polizaVersionId) throw new Error('No se encontró polizaVersionId.');

                const { items: parcialidades } = await fetchAllItems(`parcialidades/poliza-version/${polizaVersionId}`);
                this.movimientos = this.buildMovimientos(parcialidades);
            } catch {
                this.error = 'No se pudo cargar el estado de cuenta de la póliza.';
            } finally {
                this.loading = false;
            }
        },

        // Cada parcialidad se representa como un cargo (monto total) y, si ya
        // tiene algo abonado, una segunda línea de abono (monto - montoPendiente).
        buildMovimientos(parcialidades) {
            const movimientos = [];
            parcialidades
                .filter(p => getField(p, 'isActive') !== false)
                .forEach((p, index) => {
                    const monto = Number(getField(p, 'monto')) || 0;
                    const montoPendiente = Number(getField(p, 'montoPendiente')) || 0;
                    const montoPagado = monto - montoPendiente;
                    const fecha = getField(p, 'fechaVencimiento')
                        ?? getField(p, 'fechaAplicacion')
                        ?? getField(p, 'created');
                    const numero = getField(p, 'numero') ?? getField(p, 'numeroParcialidad') ?? (index + 1);
                    const id = getField(p, 'id') ?? index;

                    movimientos.push({
                        id: `${id}-cargo`,
                        concepto: `Parcialidad ${numero}`,
                        debe: true,
                        monto,
                        fechaAplicacion: fecha,
                        observaciones: estadoParcialidadLabel(getField(p, 'estado')),
                    });

                    if (montoPagado > 0) {
                        movimientos.push({
                            id: `${id}-abono`,
                            concepto: `Pago parcialidad ${numero}`,
                            debe: false,
                            monto: montoPagado,
                            fechaAplicacion: fecha,
                            observaciones: '',
                        });
                    }
                });
            return movimientos;
        },

        formatDate(val) {
            if (!val) return '-';
            return new Date(val).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
        },
        formatDateShort(val) {
            if (!val) return '-';
            const d = new Date(val);
            const day = String(d.getDate()).padStart(2, '0');
            const mon = String(d.getMonth() + 1).padStart(2, '0');
            return `${day}/${mon}/${d.getFullYear()}`;
        },
        formatCurrency(value) {
            if (value == null || value === '') return '$ -';
            return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);
        },

        print() {
            window.print();
        },
        async downloadPDF() {
            try {
                const element = this.$refs.pdfContent;
                const id = this.$route.params.id;
                const fecha = new Date().toISOString().split('T')[0];
                await html2pdf().set({
                    margin: [8, 8, 8, 8],
                    filename: `EstadoCuenta_${id}_${fecha}.pdf`,
                    image: { type: 'jpeg', quality: 0.98 },
                    html2canvas: { scale: 2, useCORS: true, logging: false },
                    jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait', compress: true },
                    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
                }).from(element).save();
            } catch {
                alert('Hubo un error al generar el PDF.');
            }
        },
    },
};
</script>

<style scoped>
.card { border-radius: 8px; }
.table th, .table td { vertical-align: middle; font-size: 13px; }
.table-light td, .table-light th { background-color: #f8f9fa; }

@media print {
    .no-print { display: none !important; }
    .card { border: 1px solid #dee2e6 !important; box-shadow: none !important; }
    .card-body { padding: 12px !important; }
    body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .table { page-break-inside: auto; }
    tr { page-break-inside: avoid; }
    .print-footer { page-break-before: avoid; }
}
</style>
